import { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { STABLE_INPUT_CLASS } from "@/lib/utils-ros";

/**
 * Inline editable field — blur-to-save (B-18).
 * Used across Universe, Signals, Leads, Opportunities, Orders.
 */
export function EditableField({
  label,
  value = "",
  onSave,
  placeholder = "Add…",
  testId,
  multiline = false,
  className = "",
  inputClassName = "",
  labelClassName = "",
  onClickStop = true,
  readOnly = false,
}) {
  const [draft, setDraft] = useState(value || "");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const focusedRef = useRef(false);

  useEffect(() => {
    if (!dirty && !focusedRef.current) setDraft(value || "");
  }, [value, dirty]);

  async function save() {
    if (readOnly || !dirty || saving) return;
    setSaving(true);
    try {
      await onSave(draft);
      setDirty(false);
    } catch {
      setDraft(value || "");
      setDirty(false);
      toast.error("Could not save — please try again");
    } finally {
      setSaving(false);
    }
  }

  const inputClass = `${STABLE_INPUT_CLASS} text-[13px] placeholder:text-[var(--text-dim)] ${multiline ? "py-1.5 min-h-[2.5rem] resize-y" : "h-8"} ${inputClassName}`;

  return (
    <div
      className={`border border-black/10 rounded-md p-2.5 ${className}`}
      data-testid={testId}
      onMouseDown={onClickStop ? (e) => e.stopPropagation() : undefined}
      onClick={onClickStop ? (e) => e.stopPropagation() : undefined}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        {label && <div className={labelClassName || "text-[10px] uppercase tracking-widest text-[var(--text-dim)]"}>{label}</div>}
        {saving && !readOnly && <Loader2 size={11} className="animate-spin text-[var(--copper)]" />}
      </div>
      {readOnly ? (
        <div className={`text-[13px] text-[var(--text)] whitespace-pre-wrap ${multiline ? "min-h-[2.5rem]" : ""} ${inputClassName}`}>
          {draft || placeholder}
        </div>
      ) : multiline ? (
        <textarea
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={() => { focusedRef.current = false; save(); }}
          placeholder={placeholder}
          rows={2}
          className={`w-full bg-transparent outline-none ${inputClass}`}
        />
      ) : (
        <input
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
          onFocus={() => { focusedRef.current = true; }}
          onBlur={() => { focusedRef.current = false; save(); }}
          placeholder={placeholder}
          className={`w-full bg-transparent outline-none ${inputClass}`}
        />
      )}
    </div>
  );
}
